import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { ReactElement, forwardRef, useMemo } from "react";

import {
  SortRule,
  TableCellSize,
  findAnswerByColumnPosition,
  sizeByColumnType,
  useExternalSiteData,
} from "~/components/database/detail/database-content/lib";
import {
  AnswerContent,
  buildAnswerContentBody,
} from "~/components/database/detail/database-content/response-table/AnswerContent";
import { DialogMenu } from "~/components/database/detail/database-content/response-table/DialogMenu";
import {
  TabHeadingButtonsProps,
  TableHeaderButton,
} from "~/components/database/detail/database-content/response-table/TableHeaderButton";
import {
  Answer,
  Response,
} from "~/components/database/detail/database-content/types";
import { compareByAnswerType } from "~/components/database/page/create/lib";
import { DatabaseDetailTableContentQuery } from "~/lib/graphql/client";
import { formatDateTime } from "~/lib/graphql/graphqlClient";
import "~/lib/monkeypatch";
import { useAppContext } from "~/pages/_app";
import { DatabaseDetailPageProps } from "~/pages/database/[id]";

const ResponseTable = forwardRef<
  HTMLDivElement,
  {
    form: DatabaseDetailPageProps["form"];
    responses: Response[];
    tabHeadings: TabHeadingButtonsProps[];
    sortRule: SortRule;
    openItemDetail: (
      itemId: DatabaseDetailTableContentQuery["form"]["responses"][number]
    ) => void;
  }
>(({ form, tabHeadings, responses, sortRule, openItemDetail }, tableRef) => {
  const theme = useTheme();
  const { currentUser } = useAppContext();
  const externalSiteData = useExternalSiteData(responses);

  const orderSortedResponses = useMemo(() => {
    const sortedResponses = [...responses].sort((responseA, responseB) => {
      if (sortRule.columnPosition === "user") {
        return responseA.user.name.localeCompare(responseB.user.name, "ja");
      }

      if (sortRule.columnPosition === "createdAt") {
        return dayjs(responseA.createdAt).diff(responseB.createdAt);
      }

      if (sortRule.columnPosition === "rowNumber") {
        return responseA.id - responseB.id;
      }

      const answerA = findAnswerByColumnPosition(
        responseA.answers,
        sortRule.columnPosition
      );

      const answerB = findAnswerByColumnPosition(
        responseB.answers,
        sortRule.columnPosition
      );

      if (answerA && answerB) {
        return compareByAnswerType(answerA, answerB);
      }

      throw new Error("invalid responses");
    });

    return sortRule.order === "asc"
      ? sortedResponses
      : sortedResponses.reverse();
  }, [sortRule, responses]);

  return (
    <div role="table" ref={tableRef} css={tableRootStyle}>
      <div role="rowgroup">
        <div role="row" css={tableHeadingStyle(theme)}>
          {tabHeadings.map(
            ({ title, onClick, onKeyDown, orderType, size }, i) => {
              return (
                <TableHeaderButton
                  key={`TabHeadingButton-${i}`}
                  title={title}
                  onClick={onClick}
                  onKeyDown={onKeyDown}
                  orderType={orderType}
                  disabled={i === 0} // NOTE: 先頭は順番を見せるだけの要素になるのでソートを無効化する
                  size={size}
                />
              );
            }
          )}
        </div>
      </div>
      <div role="rowgroup">
        {orderSortedResponses.map((response, i) => {
          const answerContentBodies: {
            component: ReactElement;
            size?: TableCellSize;
            answer?: Answer;
          }[] = [
            {
              component: <DialogMenu form={form} response={response} />,
            },
            { component: <>{i + 1}</> }, // 1-indexed
            ...response.answers
              .sort((a, b) => a.position - b.position)
              .map((answer) => {
                const externalSiteDatum =
                  answer.__typename === "UrlAnswer"
                    ? externalSiteData[answer.urlValue]
                    : undefined;

                return {
                  component: buildAnswerContentBody(answer, externalSiteDatum),
                  answer,
                  size: sizeByColumnType[answer.__typename],
                };
              }),
            {
              component: <>{response.user.name}</>,
              size: sizeByColumnType["user"],
            },
            {
              component: <>{formatDateTime(response.createdAt)}</>,
              size: sizeByColumnType["createdAt"],
            },
          ];

          return (
            <div
              role="row"
              key={`${response.id}-${i}`}
              css={answerTrStyle(theme)}
              onClick={(e) => {
                e.stopPropagation();
                openItemDetail(response);
              }}
            >
              {answerContentBodies.map((answerContentBody, i) => (
                <AnswerContent
                  key={`AnswerContent-${i}`}
                  isOwner={currentUser?.id === response.user.id}
                  answer={answerContentBody.answer}
                  size={answerContentBody.size}
                >
                  {answerContentBody.component}
                </AnswerContent>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
});
ResponseTable.displayName = "ResponseTable";

const tableRootStyle = css`
  width: max-content;
`;

const tableHeadingStyle = (theme: Theme) => css`
  position: sticky;
  top: 0;
  display: flex;
  flex-wrap: nowrap;
  background-color: ${theme.palette.background.paper};
  border-bottom: 1px solid ${theme.palette.divider};
`;

const answerTrStyle = (theme: Theme) => css`
  display: flex;
  flex-wrap: nowrap;

  &:not(:first-of-type) {
    border-top: 1px solid ${theme.palette.divider};
  }
`;

export default ResponseTable;
